// @ts-nocheck
import * as _ from 'lodash';
import {
  SlickGridModel,
  SlickPlugin,
  Formatter,
  Column,
  OnClickEventArgs,
  SlickEventHandler,
  SlickEvent,
} from 'slickgrid';
import { clicksink } from '../formatters';

export interface OnCommandEventArgs<T> {
  column: Column<T>;
  row: number;
  data: T;
  element: HTMLAnchorElement;
}

export interface CommandColumnOptions<T> extends Partial<Column<T>> {
  onCommand?: (args: OnCommandEventArgs<T>) => void;
}

const DEFAULTS = {
  name: '',
  toolTip: '',
  cssClass: '',
  width: 30,
  frozen: false,
  systemColumn: false,
  filterable: true,
  resizable: true,
  sortable: true,
  hideable: true,
};

function isAnchor(element: HTMLElement): element is HTMLAnchorElement {
  return element instanceof HTMLAnchorElement || element.closest('a');
}

function getAnchor(element: HTMLElement): HTMLAnchorElement | undefined {
  if (element instanceof HTMLAnchorElement) {
    return element;
  }

  const el = element.closest('a');

  if (el instanceof HTMLAnchorElement) {
    return el;
  }
}

function isCommandOnlyAnchor(anchor: HTMLAnchorElement): boolean {
  const href = anchor.getAttribute('href');
  if (href === null) {
    return true;
  }
  const trimmed = href.trim();
  return trimmed === '' || trimmed === '#';
}

export class CommandColumn<T> implements SlickPlugin, Column<T> {
  pluginName = 'CommandColumn' as const;

  private readonly eventHandler: SlickEventHandler;
  private grid: SlickGridModel<T>;
  private selectedRows: number[];
  private readonly callback: (args: OnCommandEventArgs<T>) => void;

  readonly onCommand: SlickEvent<OnCommandEventArgs<T>>;

  id: string;
  name?: string;
  field: string;
  rowSelector: boolean;

  formatter?: Formatter<any>;

  headerCssClass?: string;
  maxWidth?: number;
  minWidth?: number;
  rerenderOnResize?: boolean;
  resizable?: boolean;
  selectable?: boolean;
  sortable: boolean;
  hideable: boolean;
  toolTip?: string;
  width?: number;
  flags?: object;

  preventCellActivate: boolean;

  constructor(options?: CommandColumnOptions<T>) {
    _.extend(this, options);

    if (!this.formatter) {
      this.formatter = clicksink();
    }

    this.onCommand = new SlickEvent();
    this.eventHandler = new SlickEventHandler();
    this.callback = options && options.onCommand;
    this.preventCellActivate = this.flags && this.flags['preventCellActivate'];
  }

  public init(grid: SlickGridModel<T>): void {
    this.grid = grid;
    this.eventHandler.subscribe(grid.onClick, this.onClick.bind(this));
  }

  public destroy(): void {
    this.eventHandler.unsubscribeAll();
  }

  private onClick(e: Event, args: OnClickEventArgs) {
    // clicking on a row selects checkbox

    const anchor = getAnchor(e.target);

    if (this.grid.getColumns()[args.cell].id === this.id && anchor) {
      // if editing, try to commit
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (isCommandOnlyAnchor(anchor)) {
        e.preventDefault();
      }

      if (!this.preventCellActivate) {
        this.grid.setActiveCell(args.row, args.cell);
      }

      const row = args.row,
        data = this.grid.getDataItem(row);

      this.notify({
        column: this,
        row,
        data,
        element: e.target,
        value: anchor.dataset.value,
      });
    }

    return false;
  }

  private notify(args: OnCommandEventArgs<T>) {
    this.onCommand.notify(args);

    if (this.callback) {
      this.callback(args);
    }
  }
}
